import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../Components/UiComponents/Table/Table";
import Loader from "react-spinner-loader";
import { getAccidentsHistory } from "../../../Services/EndPoints";
import { getData } from "../../../Services/Services";
import { useDispatch } from "react-redux";
import { setTableData } from "../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData } from "../../../Components/UiComponents/utils";


// Define export columns configuration
const accidentHistoryColumnsForExport = [
  { key: "accidentId", label: "Accident ID" },
  { key: "vehicleNumber", label: "Vehicle Number" },
  { key: "eventType", label: "Event Type" },
  { key: "severity", label: "Severity" },
  { key: "routeName", label: "Route" },
  { key: "accidentTime", label: "Date & Time" },
  { key: "acknowledged", label: "Status" },
  { key: "speed", label: "Speed (km/h)" },
  { key: "latitude", label: "Latitude" },
  { key: "longitude", label: "Longitude" },
  { key: "address", label: "Address" },
];

const AccidentHistoryTable = (props) => {
  // Loading state for API calls
  const [loader, setLoader] = useState(false);

  // Main accident data array
  const [resourceData, setResourceData] = useState([]);
  // Filtered accident data based on search/filter
  const [resourceSrchdData, setResourceSrchdData] = useState(null);
  // Total count of filtered rows
  const [rowCount, setRowCount] = useState(0);

  // Filter states
  const [selectedSeverity, setSelectedSeverity] = useState("");
  const [selectedEventType, setSelectedEventType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [speedRange, setSpeedRange] = useState({ min: "", max: "" });
  const [searchText, setSearchText] = useState("");
  // Add this near other filter states
  const [appliedDateRange, setAppliedDateRange] = useState({ from: "", to: "" });
  const [appliedSpeedRange, setAppliedSpeedRange] = useState({ min: "", max: "" });

  // Today's date as "yyyy-MM-dd" (local), used as the max for the date inputs
  const todayStr = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Search data for table filtering
  const [searchData, setSearchData] = useState([]);

  // Format date for Excel export (dd-mm-yyyy HH:mm:ss)
  const formatDateForExport = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';

    try {
      const date = new Date(dateTimeString);

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format date as "dd-mm-yyyy"
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      // Format time as "HH:mm:ss"
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Send data to central store for export functionality
  const sendDataToCentralStore = (data) => {
    if (data && data.length > 0) {
      const columns = accidentHistoryColumnsForExport;
      // Format the data with proper date format for export
      const formattedData = data.map(item => ({
        ...item,
        accidentTime: formatDateForExport(item.accidentTime),
        acknowledged: getStatusDisplay(item.acknowledged)
      }));
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, formattedData),
        })
      );
    }
  };

  // Apply filters button handler
  const applyFilters = () => {
    if (!dateRange.from) {
      toast.warning("Please select From Date", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 1500,
      });
      return;
    }
    if (!dateRange.to) {
      toast.warning("Please select To Date", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 1500,
      });
      return;
    }
    setAppliedDateRange(dateRange);
    setAppliedSpeedRange(speedRange);
    toast.success("Filters applied successfully", {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 1000,
    });
  };

  // Update central store when search data changes
  useEffect(() => {
    if (searchData && searchData.length > 0) {
      sendDataToCentralStore(searchData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData]);

  // Re-filter data when filters or resource data changes
  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeverity, selectedEventType, selectedStatus, appliedDateRange, appliedSpeedRange, searchText, resourceData]);

  // ✅ UPDATED: CALCULATE SEVERITY BASED ON SPEED ONLY (null/empty/undefined = 0 = HIGH, 0-50 = HIGH, above 50 = CRITICAL)
  const getSeverityFromSpeed = (speed) => {
    // Handle null/undefined/empty speed values - consider as 0
    if (speed === null || speed === undefined || speed === "" || isNaN(parseFloat(speed))) {
      return "HIGH"; // Treat missing/invalid values as 0 km/h = HIGH
    }

    const speedValue = parseFloat(speed);

    // CRITICAL: Above 50 km/h
    if (speedValue > 50) {
      return "CRITICAL";
    }

    // HIGH: 0-50 km/h (including 0 and missing values)
    return "HIGH";
  };

  // Filter accident data based on selected filters
  const filterData = () => {
    let filtered = [...resourceData];

    // Filter by severity
    if (selectedSeverity) {
      filtered = filtered.filter((accident) => {
        const calculatedSeverity = getSeverityFromSpeed(accident.speed);
        return calculatedSeverity === selectedSeverity;
      });
    }

    // Filter by event type
    if (selectedEventType) {
      filtered = filtered.filter((accident) => accident.eventType === selectedEventType);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((accident) => {
        if (selectedStatus === "Active") {
          return accident.acknowledged === "ACCEPT";
        } else if (selectedStatus === "Resolved") {
          return accident.acknowledged === "RESOLVED";
        }
        return accident.acknowledged === selectedStatus;
      });
    }

    // Filter by date range (from/to are "yyyy-MM-dd" strings from native date inputs)
    if (appliedDateRange.from) {
      const [y, m, d] = appliedDateRange.from.split("-").map(Number);
      const fromDate = new Date(y, m - 1, d, 0, 0, 0, 0);
      filtered = filtered.filter((accident) => {
        const accidentDate = new Date(accident.accidentTime);
        return accidentDate >= fromDate;
      });
    }

    if (appliedDateRange.to) {
      const [y, m, d] = appliedDateRange.to.split("-").map(Number);
      const toDate = new Date(y, m - 1, d, 23, 59, 59, 999); // Include entire end date
      filtered = filtered.filter((accident) => {
        const accidentDate = new Date(accident.accidentTime);
        return accidentDate <= toDate;
      });
    }

    // ✅ UPDATED: Filter by APPLIED speed range (not current speedRange)
    if (appliedSpeedRange.min) {
      const minSpeed = parseFloat(appliedSpeedRange.min);
      if (!isNaN(minSpeed)) {
        filtered = filtered.filter((accident) => {
          const accidentSpeed = parseFloat(accident.speed) || 0;
          return accidentSpeed >= minSpeed;
        });
      }
    }

    if (appliedSpeedRange.max) {
      const maxSpeed = parseFloat(appliedSpeedRange.max);
      if (!isNaN(maxSpeed)) {
        filtered = filtered.filter((accident) => {
          const accidentSpeed = parseFloat(accident.speed) || 0;
          return accidentSpeed <= maxSpeed;
        });
      }
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((accident) =>
        accident.vehicleNumber?.toLowerCase().includes(searchLower) ||
        accident.accidentId?.toLowerCase().includes(searchLower) ||
        accident.routeName?.toLowerCase().includes(searchLower) ||
        getEventTypeDisplay(accident.eventType).toLowerCase().includes(searchLower) ||
        getStatusDisplay(accident.acknowledged).toLowerCase().includes(searchLower)
      );
    }

    setResourceSrchdData(filtered);
    setRowCount(filtered.length);
  };

  // Reset all filters
  const resetFilters = () => {
    setSelectedSeverity("");
    setSelectedEventType("");
    setSelectedStatus("");
    setDateRange({ from: "", to: "" });
    setSpeedRange({ min: "", max: "" });
    setAppliedDateRange({ from: "", to: "" });
    setAppliedSpeedRange({ min: "", max: "" });
    setSearchText("");
  };

  // Initialize component - load accident data
  useEffect(() => {
    getAccidentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle view icon click
  const handleViewAccidentDetails = (rowData) => {
    console.log("Navigating to accident details with data:", rowData);

    navigate("/RoutePlanning/AccidentViewer", {
      state: {
        accidentData: rowData,
        vehicleTrackId: rowData.vehicleTrackId || rowData.accidentId
      },
    });
  };

  // Fetch accidents list
  const getAccidentsList = async () => {
    setLoader(true);
    try {
      const accidentList = await getData(getAccidentsHistory);

      // Transform the API data to match our expected format
      const transformedData = accidentList.map(accident => ({
        ...accident,
        address: getAddressFromCoordinates(accident.latitude, accident.longitude),
        status: getStatusDisplay(accident.acknowledged),
        severity: getSeverityFromSpeed(accident.speed)
      }));

      // ✅ SORT BY DATE: Latest records first
      const sortedData = transformedData.sort((a, b) => {
        const dateA = new Date(a.accidentTime);
        const dateB = new Date(b.accidentTime);
        return dateB - dateA; // Descending order (newest first)
      });

      setLoader(false);
      setResourceData(sortedData); // Use sorted data
      setRowCount(sortedData.length);

      // Send initial data to central store for export
      sendDataToCentralStore(sortedData);
    } catch (error) {
      setLoader(false);
      console.error("Error fetching accidents list:", error);
      toast.error("Failed to fetch accidents list", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Get address from coordinates
  const getAddressFromCoordinates = (lat, lng) => {
    return `Lat: ${lat?.toFixed(6)}, Lng: ${lng?.toFixed(6)}`;
  };

  // Get status display text
  const getStatusDisplay = (acknowledged) => {
    const statusMap = {
      'ACCEPT': 'Active',
      'RESOLVED': 'Resolved',
      'PENDING': 'Pending'
    };
    return statusMap[acknowledged] || acknowledged || 'Pending';
  };

  // Format date from string to "dd-mm-yy, time" format
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';

    try {
      const date = new Date(dateTimeString);

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format date as "dd-mm-yy"
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
      const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year

      // Format time as "hh:mm AM/PM"
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';

      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      hours = String(hours).padStart(2, '0');

      return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Get event type display name
  const getEventTypeDisplay = (eventType) => {
    const eventMap = {
      'Impact': 'Impact',
      'rollover': 'Roll Over',
      'SOS': 'SOS',
      'collision': 'Collision',
      'hard_braking': 'Hard Brake',
      'sudden_acceleration': 'Sudden Acceleration',
      'overspeed': 'Over Speeding',
      'sharp_turn': 'Sharp Turn'
    };
    return eventMap[eventType] || eventType?.replace(/_/g, ' ') || 'Unknown Event';
  };

  // ✅ UPDATED SEVERITY COLOR MAPPING (Consistent with AccidentViewer)
  const getSeverityColor = (severity) => {
    const colorMap = {
      'CRITICAL': '#dc3545',    // Red - for above 50 km/h
      'HIGH': '#fd7e14',        // Orange - for 0-50 km/h (including 0, null, empty, undefined)
    };
    return colorMap[severity] || '#fd7e14'; // Default to HIGH color
  };

  // Get status color
  const getStatusColor = (status) => {
    const colorMap = {
      'ACCEPT': '#28a745',
      'RESOLVED': '#6c757d',
      'Active': '#28a745',
      'Resolved': '#6c757d',
      'Pending': '#ffc107'
    };
    return colorMap[status] || '#6c757d';
  };

  // ── Shared label style for filter section ────────────────────────────────────
  const filterLabelStyle = {
    fontSize: '11px',
    fontWeight: '600',
    color: '#4a5568',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '5px',
    display: 'block',
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: "Accident ID",
        accessor: "accidentId",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? (
            <span style={{ fontWeight: '600', color: '#1e3a5f' }} title={value}>{value}</span>
          ) : "N/A";
        },
      },
      {
        Header: "Vehicle Number",
        accessor: "vehicleNumber",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? <span title={value}>{value}</span> : "N/A";
        },
      },
      {
        Header: "Event",
        accessor: "eventType",
        disableSortBy: true,
        Cell: ({ value }) => {
          const displayType = getEventTypeDisplay(value);
          return <span title={displayType}>{displayType}</span>;
        },
      },
      {
        Header: "Severity",
        accessor: "severity",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          // Recalculate severity to ensure consistency
          const calculatedSeverity = getSeverityFromSpeed(row.original.speed);
          const color = getSeverityColor(calculatedSeverity);

          return (
            <span
              style={{
                backgroundColor: color,
                color: 'white',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.4px',
                display: 'inline-block',
                textTransform: 'uppercase',
              }}
            >
              {calculatedSeverity}
            </span>
          );
        },
      },
      {
        Header: "Route",
        accessor: "routeName",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? <span title={value}>{value}</span> : "N/A";
        },
      },
      {
        Header: "Date & Time",
        accessor: "accidentTime",
        disableSortBy: true,
        Cell: ({ value }) => {
          const formattedDate = formatDateTime(value);
          return <span title={formattedDate}>{formattedDate}</span>;
        },
      },
      {
        Header: "Status",
        accessor: "acknowledged",
        disableSortBy: true,
        Cell: ({ value }) => {
          const displayStatus = getStatusDisplay(value);
          const color = getStatusColor(value);
          return (
            <span
              style={{
                backgroundColor: `${color}18`,
                color: color,
                border: `1.5px solid ${color}55`,
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              ></span>
              {displayStatus}
            </span>
          );
        },
      },
      {
        Header: "Actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => handleViewAccidentDetails(row.original)}
                title="View Details"
                style={{
                  // backgroundColor: '#0f4a45',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="fas fa-eye" style={{ color: '#ffffff' }}></i>
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  return (
    <div className="container-fluid">
      <div className="create-user" style={{ marginTop: '4px' }}>
        <Loader
          show={loader}
          type="body"
          stack="vertical"
          message="Loading Accident Data"
        />

        {/* ── Filter Bar (inlined — never defined as a sub-component to avoid remount) ── */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderLeft: '4px solid #0f4a45',
            borderRadius: '10px',
            padding: '10px 14px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            flexWrap: 'nowrap',
          }}
        >
          {/* From Date */}
          <div style={{ flex: '0 0 136px' }}>
            <label style={filterLabelStyle}>From Date</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              max={todayStr}
              style={{ borderRadius: '7px', height: '35px', padding: '0 6px', fontSize: '12px', borderColor: '#dee2e6', width: '100%' }}
            />
          </div>

          {/* To Date */}
          <div style={{ flex: '0 0 136px' }}>
            <label style={filterLabelStyle}>To Date</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              min={dateRange.from || undefined}
              max={todayStr}
              disabled={!dateRange.from}
              style={{ borderRadius: '7px', height: '35px', padding: '0 6px', fontSize: '12px', borderColor: '#dee2e6', width: '100%' }}
            />
          </div>

          {/* Apply Button */}
          <button
            onClick={applyFilters}
            style={{
              flexShrink: 0, borderRadius: '7px', border: 'none',
              color: '#ffffff', background: 'linear-gradient(135deg, #28a745 0%, #1e8035 100%)',
              fontWeight: '600', padding: '8px 14px', fontSize: '12px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '1', alignSelf: 'flex-end',
              boxShadow: '0 2px 5px rgba(40,167,69,0.28)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
              <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Apply
          </button>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            style={{
              flexShrink: 0, borderRadius: '7px', border: 'none',
              color: '#ffffff', background: 'linear-gradient(135deg, #1a7a73 0%, #3888a5 100%)',
              fontWeight: '600', padding: '8px 14px', fontSize: '12px',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '1', alignSelf: 'flex-end',
              boxShadow: '0 2px 5px rgba(72,153,183,0.28)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
              <path d="M10 2a5 5 0 1 0 .5 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="10,0 10,4 6,4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Reset
          </button>

          {/* Vertical divider */}
          <div style={{ width: '1px', height: '35px', background: '#e2e8f0', flexShrink: 0, alignSelf: 'flex-end' }}></div>

          {/* Event Type */}
          <div style={{ flex: '0 0 114px' }}>
            <label style={filterLabelStyle}>Event Type</label>
            <select
              className="form-select"
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
              style={{ borderRadius: '7px', height: '35px', padding: '0 6px', fontSize: '12px', borderColor: '#dee2e6' }}
            >
              <option value="">All Events</option>
              <option value="Impact">Impact</option>
              <option value="SOS">SOS</option>
              <option value="rollover">Roll Over</option>
            </select>
          </div>

          {/* Severity */}
          <div style={{ flex: '0 0 104px' }}>
            <label style={filterLabelStyle}>Severity</label>
            <select
              className="form-select"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              style={{ borderRadius: '7px', height: '35px', padding: '0 6px', fontSize: '12px', borderColor: '#dee2e6' }}
            >
              <option value="">All Severity</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
            </select>
          </div>

          {/* Status */}
          <div style={{ flex: '0 0 104px' }}>
            <label style={filterLabelStyle}>Status</label>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ borderRadius: '7px', height: '35px', padding: '0 6px', fontSize: '12px', borderColor: '#dee2e6' }}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>

          {/* Vertical divider */}
          <div style={{ width: '1px', height: '35px', background: '#e2e8f0', flexShrink: 0, alignSelf: 'flex-end' }}></div>

          {/* Search */}
          <div style={{ flex: '1', minWidth: '130px' }}>
            <label style={filterLabelStyle}>Search</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  borderRadius: '7px', height: '35px',
                  padding: '0 32px 0 10px', fontSize: '12px',
                  border: '1px solid #dee2e6', width: '100%',
                  outline: 'none', color: '#333',
                }}
              />
              {searchText ? (
                <i
                  className="fas fa-times"
                  onClick={() => setSearchText("")}
                  style={{
                    position: 'absolute', right: '9px', top: '50%',
                    transform: 'translateY(-50%)', color: '#6c757d',
                    cursor: 'pointer', fontSize: '11px',
                  }}
                ></i>
              ) : (
                <i
                  className="fas fa-search"
                  style={{
                    position: 'absolute', right: '9px', top: '50%',
                    transform: 'translateY(-50%)', color: '#0f4a45',
                    pointerEvents: 'none', fontSize: '11px',
                  }}
                ></i>
              )}
            </div>
          </div>
        </div>

        <div className="datatable__container accident-history-table">
          {resourceData.length > 0 ? (
            <Table
              data={resourceSrchdData || resourceData}
              columns={columns}
              numberOfRows={10}
              showExport={false}
              showSearch={false}
              setSearchData={setSearchData}
              showFilter={false}
              showReset={false}
              onExpand={false}
              rowCount={rowCount}
              headerTableHeadStyle={{
                background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)",
                color: "#FFFFFF",
              }}
              tableStyle={{
                border: "1px solid #e8ecf0",
                borderRadius: "8px",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "calc(100vh - 295px)",
                boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
              }}
            />
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No Accident Data Available</h5>
            </div>
          )}
        </div>
      </div>

      <style>
      {`
        .grid-stack>.grid-stack-item>.grid-stack-item-content {
          margin: 0;
          position: absolute;
          width: auto;
          overflow-y: hidden;
        }

        /* Ensure proper spacing for table container */
        .datatable__container {
          margin-top: 30px;
          margin-bottom: 6px;
          overflow: visible;
        }

        /* Hide the empty table header area (no search/export/reset used here) */
        .accident-history-table .iccc__table-header {
          display: none !important;
        }

        /* ── Accident History Table UI enhancements ──────────────────── */

        .accident-history-table .iccc__table--container {
        padding: 0px !important;
        border-radius: 6px !important;
        margin: 0px !important;
        // border-bottom: 1px solid #e8ecf0 !important;
        }

        /* Header: uppercase, tighter font, generous padding, sticky on scroll */
        .accident-history-table .iccc--table thead th {
          padding: 13px 16px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          letter-spacing: 0.7px !important;
          text-transform: uppercase !important;
          border: none !important;
          white-space: nowrap !important;
          border-bottom: 2px solid rgba(255,255,255,0.15) !important;
          background: linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%) !important;
        }

        /* Remove ALL row / cell borders */
        .accident-history-table .iccc--table tbody tr,
        .accident-history-table .iccc--table tbody td {
          border: none !important;
          border-color: transparent !important;
        }
        .accident-history-table .table > :not(caption) > * > * {
          border-bottom-width: 0 !important;
        }

        /* Body cell padding */
        .accident-history-table .iccc--table tbody td {
          padding: 10px 16px !important;
          vertical-align: middle !important;
        }



        /* Alternating row shading */
        .accident-history-table .iccc--table tbody tr:nth-child(odd) td {
          background-color: #ffffff !important;
        }
        .accident-history-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #f4f8fd !important;
        }

        /* Row hover */
        .accident-history-table .iccc--table tbody tr:hover td {
          background-color: #e3eef9 !important;
          transition: background-color 0.12s ease;
        }
        .accident-history-table .iccc--table tbody tr:hover td:first-child {
          box-shadow: inset 3px 0 0 #0f4a45 !important;
        }
      `}
    </style>

    </div>
  );
};

export default AccidentHistoryTable;


